<template>
  <div class="smooth-draw">
<div class="drawing-container">
      <canvas
        ref="canvas"
        @pointerdown="startDrawing"
        @pointermove="draw"
        @pointerup="endDrawing"
        @pointerleave="endDrawing"
        class="drawing-canvas"
      ></canvas>

      <!-- Vista previa cuando se usa pad Wacom -->
      <img
        v-if="wacom && wacomImage"
        :src="wacomImage"
        alt="Firma Wacom"
        class="wacom-preview"
      />
      
      <!-- Overlay para mostrar información cuando está vacío (solo modo mouse) -->
      <div v-if="allLines.length === 0 && !isDrawing && !wacomImage" class="canvas-placeholder">
        <div class="placeholder-content">
          <svg class="placeholder-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M12 19l7-7 3 3-7 7-3-3z"/>
            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
            <path d="M2 2l7.586 7.586"/>
          </svg>
          <h3>Comienza a dibujar</h3>
          <p>Usa tu mouse, stylus o dedo para dibujar</p>
        </div>
      </div>
    </div>

    <!-- Panel de controles moderno -->
    <div class="controls-panel">
      <div class="control-section" v-if="!wacom">
        <label class="control-label">Color</label>
        <div class="color-input-wrapper">
          <input type="color" v-model="strokeColor" class="color-input" />
          <div class="color-preview" :style="{ backgroundColor: strokeColor }"></div>
        </div>
      </div>

      <div class="control-section" v-if="!wacom">
        <label class="control-label">Grosor</label>
        <div class="size-control">
          <input 
            type="range" 
            min="1" 
            max="20" 
            v-model.number="strokeSize" 
            class="size-slider"
          />
          <div class="size-preview">
            <div 
              class="size-dot" 
              :style="{ 
                width: strokeSize + 'px', 
                height: strokeSize + 'px',
                backgroundColor: strokeColor
              }"
            ></div>
          </div>
        </div>
        <span class="size-value">{{ strokeSize }}px</span>
      </div>

      <div class="control-actions">
        <!-- Botón para capturar desde pad Wacom y acción de reintento -->
        <div v-if="wacom" class="wacom-action-container">
          <button
            type="button"
            @click="captureWacom"
            class="btn btn-secondary btn-wacom-action"
            :class="{ 'btn-wacom-connected': wacomReady }"
            :disabled="wacomBusy || (!wacomReady && wacomStatus === 'connecting')"
          >
            <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M4 4h16v12H4z"/>
              <path d="M8 20h8"/>
              <path d="M12 16v4"/>
            </svg>
            <span>{{ wacomButtonText }}</span>
          </button>

          <button
            v-if="!wacomReady"
            type="button"
            @click="retryWacomConnection"
            class="btn btn-secondary btn-retry"
            title="Reintentar conexión con pad Wacom"
            :disabled="wacomStatus === 'connecting'"
          >
            <svg class="btn-icon" :class="{ 'spin-icon': wacomStatus === 'connecting' }" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            <span class="btn-retry-text">Reintentar</span>
          </button>
        </div>

        <button @click="clearCanvas" class="btn btn-secondary">
          <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 6h18"/>
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
          </svg>
          Limpiar
        </button>
        
        <button @click="emitImage" class="btn btn-primary" :disabled="!canSave">
          <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
            <polyline points="17,21 17,13 7,13 7,21"/>
            <polyline points="7,3 7,8 15,8"/>
          </svg>
          Guardar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import getStroke from "perfect-freehand";

export default {
  name: "SmoothDraw",
  props: {
    // Si es true, usa el pad Wacom (SigCaptX). Si es false, usa el canvas con mouse.
    wacom: {
      type: Boolean,
      default: false,
    },
    // Texto opcional que se envía al dispositivo (por ejemplo, nombre del paciente)
    wacomFullName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isDrawing: false,
      points: [],
      allLines: [], // para guardar varios strokes
      strokeSize: 4,
      strokeColor: "#000000",
      ctx: null,
      // Modo Wacom
      wacomImage: null,
      wacomBusy: false,
      wacomReady: false,
      wacomStatus: "idle", // 'idle' | 'connecting' | 'ready' | 'error'
      wacomRetryTimeout: null,
      wacomRetryCount: 0,
    };
  },
  mounted() {
    this.$nextTick(() => {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      this.ctx = canvas.getContext("2d");
      this.resizeCanvas();
      window.addEventListener("resize", this.resizeCanvas);

      if (this.wacom) {
        this.ensureWacomReady();
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeCanvas);
    if (this.wacomRetryTimeout) {
      clearTimeout(this.wacomRetryTimeout);
      this.wacomRetryTimeout = null;
    }
  },
  watch: {
    // Si el componente se reutiliza y cambia el flag, reintentar inicialización
    wacom(newVal) {
      if (newVal) {
        this.$nextTick(() => this.ensureWacomReady());
      } else {
        this.wacomReady = false;
        this.wacomStatus = "idle";
        if (this.wacomRetryTimeout) {
          clearTimeout(this.wacomRetryTimeout);
          this.wacomRetryTimeout = null;
        }
      }
    },
  },
  computed: {
    wacomButtonText() {
      if (this.wacomBusy) return "Capturando...";
      if (this.wacomReady) return "Capturar desde pad";
      if (this.wacomStatus === "connecting") return "Conectando al pad...";
      return "Pad no detectado";
    },
    canSave() {
      // Guardar debe funcionar tanto con firma manual como con captura Wacom
      const hasManual = this.allLines.length > 0;
      const hasWacom = !!this.wacomImage;
      return (hasManual || hasWacom) && !this.wacomBusy;
    },
  },
  methods: {
    retryWacomConnection() {
      this.wacomRetryCount = 0;
      if (this.wacomRetryTimeout) {
        clearTimeout(this.wacomRetryTimeout);
        this.wacomRetryTimeout = null;
      }
      this.wacomStatus = "connecting";
      this.wacomReady = false;
      this.initWacom(true);
    },
    ensureWacomReady() {
      // Determinar si el servicio ya está corriendo y tenemos objetos listos
      const sdkCtor = window.WacomGSS_SignatureSDK;
      if (!sdkCtor) {
        this.wacomReady = false;
        this.wacomStatus = "error";
        return;
      }

      const sdkInstance = window.wgssSignatureSDK;
      const isRunning = !!(sdkInstance && sdkInstance.running);

      // Si ya corre, basta con validar que existan sigCtl y dynCapt
      if (isRunning) {
        const hasObjects = !!(window.sigCtl && window.dynCapt);
        if (hasObjects) {
          this.wacomReady = true;
          this.wacomStatus = "ready";
          return;
        }
        // Corre pero faltan objetos: reintentar inicialización
        this.initSigCtl();
        return;
      }

      // No está corriendo todavía: inicializar SDK
      this.initWacom();
    },
    resizeCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      if (!this.ctx) {
        this.ctx = canvas.getContext("2d");
      }

      this.redrawAll();
    },
    startDrawing(e) {
      // Si el usuario empieza a dibujar manualmente, asumimos que quiere modo manual
      // y limpiamos la captura Wacom (si existiera) para evitar confusión visual.
      if (this.wacomImage) {
        this.wacomImage = null;
      }
      this.isDrawing = true;
      this.points = [this.getPoint(e)];
    },
    draw(e) {
      if (!this.isDrawing) return;
      this.points.push(this.getPoint(e));
      this.redrawAll(); // Limpia y vuelve a dibujar todo
      this.drawStroke(this.points, this.strokeColor, this.strokeSize);
    },
    endDrawing() {
      if (!this.isDrawing) return;
      this.isDrawing = false;
      this.allLines.push({
        points: [...this.points],
        color: this.strokeColor,
        size: this.strokeSize,
      });
      this.points = [];
    },
    getPoint(e) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      return [e.clientX - rect.left, e.clientY - rect.top];
    },
    drawStroke(points, color, size) {
      const ctx = this.ctx;
      const stroke = getStroke(points, {
        size: this.strokeSize,
        thinning: 0.5,
        smoothing: 0.5,
        streamline: 0.5,
        startTaper: 50,    // grosor se reduce al inicio
        endTaper: 50,      // grosor se reduce al final
        taperEasing: (t) => t, // función de easing (opcional, por defecto es lineal)
        capStart: true,    // true para borde redondeado al inicio
        capEnd: true       // true para borde redondeado al final
      });

      if (!stroke.length) return;

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(stroke[0][0], stroke[0][1]);

      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i][0], stroke[i][1]);
      }

      ctx.closePath();
      ctx.fill();
    },
    redrawAll() {
      if (!this.ctx) return;
      this.ctx.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);
      for (const line of this.allLines) {
        this.drawStroke(line.points, line.color, line.size);
      }
    },
    clearCanvas() {
      // Limpiar siempre el canvas; si hay captura Wacom también la limpiamos
      this.wacomImage = null;
      this.allLines = [];
      this.ctx.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);
    },
    emitImage() {
      // Si existe captura Wacom, recortar márgenes blancos vacíos antes de emitir
      if (this.wacomImage) {
        const img = new Image();
        img.onload = () => {
          try {
            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = img.naturalWidth || img.width;
            tempCanvas.height = img.naturalHeight || img.height;
            const tempCtx = tempCanvas.getContext("2d");
            tempCtx.drawImage(img, 0, 0);

            const imgData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
            const pixels = imgData.data;
            let minX = tempCanvas.width, minY = tempCanvas.height, maxX = 0, maxY = 0;

            for (let y = 0; y < tempCanvas.height; y++) {
              for (let x = 0; x < tempCanvas.width; x++) {
                const idx = (y * tempCanvas.width + x) * 4;
                const r = pixels[idx], g = pixels[idx + 1], b = pixels[idx + 2], a = pixels[idx + 3];
                // Detectar trazo (no blanco ni transparente)
                if (a > 20 && (r < 230 || g < 230 || b < 230)) {
                  if (x < minX) minX = x;
                  if (y < minY) minY = y;
                  if (x > maxX) maxX = x;
                  if (y > maxY) maxY = y;
                }
              }
            }

            if (maxX > minX && maxY > minY) {
              const pad = 6;
              const cropX = Math.max(0, minX - pad);
              const cropY = Math.max(0, minY - pad);
              const cropW = Math.min(tempCanvas.width - cropX, (maxX - minX) + pad * 2);
              const cropH = Math.min(tempCanvas.height - cropY, (maxY - minY) + pad * 2);

              const cropCanvas = document.createElement("canvas");
              cropCanvas.width = cropW;
              cropCanvas.height = cropH;
              const cropCtx = cropCanvas.getContext("2d");
              cropCtx.drawImage(tempCanvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

              this.$emit("save-signature", cropCanvas.toDataURL("image/png"));
            } else {
              this.$emit("save-signature", this.wacomImage);
            }
          } catch (e) {
            this.$emit("save-signature", this.wacomImage);
          }
        };
        img.onerror = () => {
          this.$emit("save-signature", this.wacomImage);
        };
        img.src = this.wacomImage;
        return;
      }
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");

      // Obtener todos los píxeles
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;

      let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;

      // Buscar el área ocupada por trazos
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const index = (y * canvas.width + x) * 4; // RGBA
          if (pixels[index + 3] > 0) { // Si hay algo dibujado (alpha > 0)
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }

      // Manejar el caso de que no haya dibujo
      if (maxX - minX <= 0 || maxY - minY <= 0) {
        this.$emit("save-signature", null);
        return;
      }

      // Crear un canvas temporal con solo el recorte
      const cropWidth = maxX - minX + 1;
      const cropHeight = maxY - minY + 1;

      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = cropWidth;
      tempCanvas.height = cropHeight;
      const tempCtx = tempCanvas.getContext("2d");

      // Copiar la parte dibujada al nuevo canvas
      tempCtx.putImageData(
        ctx.getImageData(minX, minY, cropWidth, cropHeight),
        0,
        0
      );

      // Emitir la imagen recortada
      const base64 = tempCanvas.toDataURL("image/png");
      this.$emit("save-signature", base64);
    },

    initWacom(force = false) {
      if (!window.WacomGSS_SignatureSDK) {
        console.warn("Wacom SDK scripts not loaded.");
        this.wacomReady = false;
        this.wacomStatus = "error";
        return;
      }

      if (this.wacomRetryTimeout) {
        clearTimeout(this.wacomRetryTimeout);
        this.wacomRetryTimeout = null;
      }

      // Check if already initialized and running
      if (!force && window.wgssSignatureSDK && window.wgssSignatureSDK.running) {
        if (window.sigCtl && window.dynCapt) {
          this.wacomReady = true;
          this.wacomStatus = "ready";
        } else {
          this.initSigCtl();
        }
        return;
      }

      this.wacomStatus = "connecting";
      this.wacomReady = false;

      const onDetectRunning = () => {
        console.log("Wacom SDK Service detected.");
        if (this.wacomRetryTimeout) {
          clearTimeout(this.wacomRetryTimeout);
          this.wacomRetryTimeout = null;
        }
        if (window.wgssSignatureSDK && window.wgssSignatureSDK.running) {
          this.initSigCtl();
        }
      };

      // Initialize SDK
      const port = window.SERVICEPORT || 8000;
      window.wgssSignatureSDK = new window.WacomGSS_SignatureSDK(onDetectRunning, port);

      // Watchdog de reintento automático si el servicio tarda en responder
      this.wacomRetryTimeout = setTimeout(() => {
        if (!this.wacomReady) {
          if (this.wacomRetryCount < 3) {
            this.wacomRetryCount++;
            console.log("Wacom no respondió a tiempo. Reintentando intento " + this.wacomRetryCount + "...");
            this.initWacom(true);
          } else {
            console.warn("Wacom SDK no disponible tras reintentos.");
            this.wacomStatus = "error";
          }
        }
      }, 2500);
    },

    initSigCtl() {
      const sdk = window.wgssSignatureSDK;
      if (!sdk) {
        console.error("Wacom SDK no inicializado");
        this.wacomReady = false;
        this.wacomStatus = "error";
        return;
      }

      // Misma licencia que usa el ejemplo de Wacom (SigCaptX-Globals.js)
      const LICENCEKEY =
        "eyJhbGciOiJSUzUxMiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiI3YmM5Y2IxYWIxMGE0NmUxODI2N2E5MTJkYTA2ZTI3NiIsImV4cCI6MjE0NzQ4MzY0NywiaWF0IjoxNTYwOTUwMjcyLCJyaWdodHMiOlsiU0lHX1NES19DT1JFIiwiU0lHQ0FQVFhfQUNDRVNTIl0sImRldmljZXMiOlsiV0FDT01fQU5ZIl0sInR5cGUiOiJwcm9kIiwibGljX25hbWUiOiJTaWduYXR1cmUgU0RLIiwid2Fjb21faWQiOiI3YmM5Y2IxYWIxMGE0NmUxODI2N2E5MTJkYTA2ZTI3NiIsImxpY191aWQiOiJiODUyM2ViYi0xOGI3LTQ3OGEtYTlkZS04NDlmZTIyNmIwMDIiLCJhcHBzX3dpbmRvd3MiOltdLCJhcHBzX2lvcyI6W10sImFwcHNfYW5kcm9pZCI6W10sIm1hY2hpbmVfaWRzIjpbXX0.ONy3iYQ7lC6rQhou7rz4iJT_OJ20087gWz7GtCgYX3uNtKjmnEaNuP3QkjgxOK_vgOrTdwzD-nm-ysiTDs2GcPlOdUPErSp_bcX8kFBZVmGLyJtmeInAW6HuSp2-57ngoGFivTH_l1kkQ1KMvzDKHJbRglsPpd4nVHhx9WkvqczXyogldygvl0LRidyPOsS5H2GYmaPiyIp9In6meqeNQ1n9zkxSHo7B11mp_WXJXl0k1pek7py8XYCedCNW5qnLi4UCNlfTd6Mk9qz31arsiWsesPeR9PN121LBJtiPi023yQU8mgb9piw_a-ccciviJuNsEuRDN3sGnqONG3dMSA";

      const onDynCaptConstructor = (dynCaptV, status) => {
        if (sdk.ResponseStatus.OK === status) {
          window.dynCapt = dynCaptV;
          console.log("Wacom SigCtl (licenciado) y DynCapt inicializados.");
          this.wacomReady = true;
          this.wacomStatus = "ready";
          this.wacomRetryCount = 0;
          if (this.wacomRetryTimeout) {
            clearTimeout(this.wacomRetryTimeout);
            this.wacomRetryTimeout = null;
          }
        } else {
          console.error("Error creando DynCapt:", status);
          this.wacomReady = false;
          this.wacomStatus = "error";
        }
      };

      const onPutLicence = (sigCtlV, status) => {
        if (sdk.ResponseStatus.OK === status) {
          try {
            window.dynCapt = new sdk.DynamicCapture(onDynCaptConstructor);
          } catch (e) {
            console.error("Excepción creando DynamicCapture:", e);
            this.wacomReady = false;
            this.wacomStatus = "error";
          }
        } else {
          console.error("Error aplicando licencia SigCtl:", status);
          this.wacomReady = false;
          this.wacomStatus = "error";
        }
      };

      const onSigCtlConstructor = (sigCtlV, status) => {
        if (sdk.ResponseStatus.OK === status) {
          window.sigCtl = sigCtlV;
          try {
            // Activar licencia en SigCtl como hace el ejemplo
            sigCtlV.PutLicence(LICENCEKEY, onPutLicence);
          } catch (e) {
            console.error("Excepción en SigCtl.PutLicence:", e);
            this.wacomReady = false;
            this.wacomStatus = "error";
          }
        } else {
          console.error("Error creando SigCtl:", status);
          this.wacomReady = false;
          this.wacomStatus = "error";
        }
      };

      window.sigCtl = new sdk.SigCtl(onSigCtlConstructor);
    },

    // Captura la firma usando el pad Wacom (SigCaptX)
    captureWacom() {
      if (!this.wacom) return;

      // Si no está listo, reintentar conexión inmediatamente
      if (!this.wacomReady) {
        this.retryWacomConnection();
        return;
      }

  const sdk = window.wgssSignatureSDK;
  const dynCapt = window.dynCapt;
  const sigCtl = window.sigCtl;

  if (!sdk || !dynCapt || !sigCtl) {
    console.error("Servicio de firma Wacom (SigCaptX) no disponible");
    this.wacomReady = false;
    if (this.$fun && this.$fun.alert) {
      this.$fun.alert(
        "Servicio de firma Wacom no disponible. Verifique SigCaptX.",
        "error"
      );
    }
    return;
  }

  this.wacomBusy = true;

  // Usaremos este nombre como WHO y también como datos en el hash
  const fullName = this.wacomFullName || "";
  const reason = "Historia Clínica";

  const onRenderBitmap = (sigObjV, bmpObj, status) => {
    if (status === sdk.ResponseStatus.OK) {
      // bmpObj es base64 porque usamos RenderOutputBase64
      this.wacomImage = "data:image/png;base64," + bmpObj;
    } else {
      console.error("Signature RenderBitmap error:", status);
      if (this.$fun && this.$fun.alert) {
        this.$fun.alert(
          "Error al renderizar la firma desde el pad.",
          "error"
        );
      }
    }
    this.wacomBusy = false;
  };

  const onDynCaptCapture = (dynCaptV, sigObjV, status) => {
    if (status === sdk.DynamicCaptureResult.DynCaptOK) {
      const box = this.$el.querySelector(".drawing-canvas");
      const width = box ? box.clientWidth : 400;
      const height = box ? box.clientHeight : 150;

      const flags =
        sdk.RBFlags.RenderOutputBase64 | sdk.RBFlags.RenderColor32BPP;

      // IMPORTANTE: el SDK espera formatos como "bmp"/"png", no MIME "image/png".
      // El sample usa BITMAP_IMAGEFORMAT = "bmp", así que replicamos eso.
      sigObjV.RenderBitmap(
        "bmp",
        width,
        height,
        0.7, // grosor de tinta
        0x00000000, // tinta negra
        0x00ffffff, // fondo blanco (como BITMAP_BACKGROUNDCOLOR)
        flags,
        4,
        4,
        onRenderBitmap
      );
    } else if (status === sdk.DynamicCaptureResult.DynCaptCancel) {
      // usuario canceló
      this.wacomBusy = false;
    } else {
      console.error("Wacom capture error:", status);
      if (this.$fun && this.$fun.alert) {
        this.$fun.alert(
          "Error al capturar firma desde el pad. Código: " + status,
          "error"
        );
      }
      this.wacomBusy = false;
    }
  };

  // ---- Preparar el Hash igual que hace el ejemplo (Clear + PutType + Add datos) ----

  // Paso 3: luego de añadir los datos al hash, recién llamamos a Capture
  const onAddLname = (hashObj, status) => {
    if (status === sdk.ResponseStatus.OK) {
      try {
        dynCapt.Capture(
          sigCtl,
          fullName,
          reason,
          hashObj,
          null,
          onDynCaptCapture
        );
      } catch (e) {
        console.error("Excepción en dynCapt.Capture:", e);
        this.wacomBusy = false;
      }
    } else {
      console.error("Hash Add(lname) error:", status);
      this.wacomBusy = false;
    }
  };

  const onAddFname = (hashObj, status) => {
    if (status === sdk.ResponseStatus.OK) {
      try {
        const vLname = new sdk.Variant();
        vLname.Set(""); // podemos dejar apellido vacío
        hashObj.Add(vLname, onAddLname);
      } catch (e) {
        console.error("Excepción en Hash.Add(lname):", e);
        this.wacomBusy = false;
      }
    } else {
      console.error("Hash Add(fname) error:", status);
      this.wacomBusy = false;
    }
  };

  const onPutType = (hashObj, status) => {
    if (status === sdk.ResponseStatus.OK) {
      try {
        // Igual que en el ejemplo: meter 2 cadenas al hash
        const vFname = new sdk.Variant();
        vFname.Set(fullName); // usamos el nombre completo como "fname"

        hashObj.Add(vFname, onAddFname);
      } catch (e) {
        console.error("Excepción en Hash.Add(fname):", e);
        this.wacomBusy = false;
      }
    } else {
      console.error("Hash PutType error:", status);
      this.wacomBusy = false;
    }
  };

  const onClear = (hashObj, status) => {
    if (status === sdk.ResponseStatus.OK) {
      // Definimos tipo de hash MD5, igual que SigVerify
      hashObj.PutType(sdk.HashType.HashMD5, onPutType);
    } else {
      console.error("Hash Clear error:", status);
      this.wacomBusy = false;
    }
  };

  const onHashConstructor = (hashObj, status) => {
    if (status === sdk.ResponseStatus.OK) {
      // Limpiamos el hash antes de usarlo
      hashObj.Clear(onClear);
    } else {
      console.error("Hash constructor error:", status);
      this.wacomBusy = false;
    }
  };

  try {
    // Creamos el Hash y seguimos el flujo: Clear -> PutType -> Add(fname,lname) -> Capture
    new sdk.Hash(onHashConstructor);
  } catch (e) {
    console.error("Excepción creando Hash Wacom:", e);
    this.wacomBusy = false;
  }
},
  },
};
</script>

<style scoped>
.smooth-draw {
  position: relative;
  width: 100%;
  height: 100vh;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  display: flex;
  flex-direction: column;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.drawing-container {
  position: relative;
  flex: 1;
  margin: 5px;
  margin-bottom: 10px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.1);
  background: white;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.drawing-canvas {
  width: 100%;
  height: 100%;
  touch-action: none;
  background: white;
  display: block;
  cursor: crosshair;
  border-radius: 16px;
}

.wacom-preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}

.canvas-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
}

.placeholder-content {
  text-align: center;
  color: #94a3b8;
  opacity: 0.7;
}

.placeholder-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  display: block;
  stroke-width: 1.5;
}

.placeholder-content h3 {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 600;
  color: #64748b;
}

.placeholder-content p {
  margin: 0;
  font-size: 16px;
  color: #94a3b8;
}

.controls-panel {
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  flex-wrap: wrap;
}

.control-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.control-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.color-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-input {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  opacity: 0;
  position: absolute;
}

.color-input:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.color-preview {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 3px solid white;
  pointer-events: none;
}

.color-input-wrapper:hover .color-preview {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.size-control {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 200px;
}

.size-slider {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.size-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1976d2;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(99, 144, 241, 0.4);
  transition: all 0.2s ease;
}

.size-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 4px 12px rgba(99, 144, 241, 0.4);
}

.size-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1976d2;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 6px rgba(99, 144, 241, 0.4);
}

.size-preview {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.size-dot {
  border-radius: 50%;
  transition: all 0.2s ease;
  min-width: 2px;
  min-height: 2px;
  max-width: 32px;
  max-height: 32px;
}

.size-value {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  min-width: 30px;
  text-align: center;
}

.control-actions {
  display: flex;
  gap: 12px;
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-icon {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.btn-primary {
  /* background: linear-gradient(135deg, #6366f1, #8b5cf6); */
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: white;
  color: #6b7280;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.btn-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  background: #f9fafb;
}

.wacom-action-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-wacom-connected {
  border-color: #4caf50;
  color: #2e7d32;
}

.btn-retry {
  padding: 12px 14px;
}

.btn-retry-text {
  font-size: 12px;
}

.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .controls-panel {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }
  
  .control-section {
    width: 100%;
  }
  
  .size-control {
    min-width: auto;
    width: 100%;
  }
  
  .control-actions {
    width: 100%;
    justify-content: center;
  }
  
  .btn {
    flex: 1;
    justify-content: center;
  }
}
</style>